import { useMemo } from "react";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";
import styles from "./io-form.module.scss";

const IORelations = ({ formData, lookupOptions, onUpdateField }) => {
	const isApplication = formData.application_type_id === 1;
	const isTransfer = formData.application_type_id === 2;

	const filteredEstates = useMemo(() => {
		if (!formData.estatesParents || formData.estatesParents.length === 0) {
			return lookupOptions.estates;
		}

		return lookupOptions.estates.filter((estate) => formData.estatesParents.includes(estate.estate_parent_id));
	}, [formData.estatesParents, lookupOptions.estates]);

	const filteredCategories = useMemo(() => {
		if (!formData.estates || formData.estates.length === 0) {
			return lookupOptions.categories;
		}
		return lookupOptions.categories.filter((category) => formData.estates.includes(category.estate_id));
	}, [formData.estates, lookupOptions.categories]);

	const filteredSpecies = useMemo(() => {
		if (!formData.categories || formData.categories.length === 0) {
			return lookupOptions.species;
		}
		return lookupOptions.species.filter((specie) => formData.categories.includes(specie.category_id));
	}, [formData.categories, lookupOptions.species]);

	const filteredVarieties = useMemo(() => {
		if (!formData.species || formData.species.length === 0) {
			return lookupOptions.varieties;
		}
		return lookupOptions.varieties.filter((variety) => formData.species.includes(variety.specie_id));
	}, [formData.species, lookupOptions.varieties]);

	const filteredQuarters = useMemo(() => {
		if (!formData.varieties || formData.varieties.length === 0) {
			return lookupOptions.quarters;
		}
		return lookupOptions.quarters.filter((quarter) => formData.varieties.includes(quarter.variety_id));
	}, [formData.varieties, lookupOptions.quarters]);

	const filteredBusinessCenters = useMemo(() => {
		const hasEstates = formData.estates && formData.estates.length > 0;
		const hasCategories = formData.categories && formData.categories.length > 0;
		const hasVarieties = formData.varieties && formData.varieties.length > 0;

		if (!hasEstates && !hasCategories && !hasVarieties) {
			return lookupOptions.businessCenters;
		}

		return lookupOptions.businessCenters.filter((bc) => {
			const matchesEstates = !hasEstates || (bc.estates && bc.estates.some((e) => formData.estates.includes(e)));
			const matchesCategories = !hasCategories || (bc.category_id && formData.categories.includes(bc.category_id));
			const matchesVarieties = !hasVarieties || (bc.crop_detail_id && formData.varieties.includes(bc.crop_detail_id));

			return matchesEstates && matchesCategories && matchesVarieties;
		});
	}, [formData.estates, formData.categories, formData.varieties, lookupOptions.businessCenters]);

	const handleEstatesParentsChange = (value) => {
		onUpdateField("estatesParents", value);
		const validEstates = filteredEstates.filter((e) => value.includes(e.estate_parent_id)).map((e) => e.value);
		if (validEstates.length !== formData.estates?.length) {
			onUpdateField("estates", validEstates);
		}
	};

	const handleEstatesChange = (value) => {
		onUpdateField("estates", value);
		const validCategories = filteredCategories.filter((c) => value.includes(c.estate_id)).map((c) => c.value);
		if (validCategories.length !== formData.categories?.length) {
			onUpdateField("categories", validCategories);
		}
		const validBusinessCenters = filteredBusinessCenters.filter((bc) => bc.estates && bc.estates.some((e) => value.includes(e))).map((bc) => bc.value);
		if (validBusinessCenters.length !== formData.businessCenters?.length) {
			onUpdateField("businessCenters", validBusinessCenters);
		}
	};

	const handleCategoriesChange = (value) => {
		onUpdateField("categories", value);
		const validSpecies = filteredSpecies.filter((s) => value.includes(s.category_id)).map((s) => s.value);
		if (validSpecies.length !== formData.species?.length) {
			onUpdateField("species", validSpecies);
		}
	};

	const handleSpeciesChange = (value) => {
		onUpdateField("species", value);
		const validVarieties = filteredVarieties.filter((v) => value.includes(v.specie_id)).map((v) => v.value);
		if (validVarieties.length !== formData.varieties?.length) {
			onUpdateField("varieties", validVarieties);
		}
	};

	const handleVarietiesChange = (value) => {
		onUpdateField("varieties", value);
		const validQuarters = filteredQuarters.filter((q) => value.includes(q.variety_id)).map((q) => q.value);
		if (validQuarters.length !== formData.quarters?.length) {
			onUpdateField("quarters", validQuarters);
		}
		const validBusinessCenters = filteredBusinessCenters.filter((bc) => bc.crop_detail_id && value.includes(bc.crop_detail_id)).map((bc) => bc.value);
		if (validBusinessCenters.length !== formData.businessCenters?.length) {
			onUpdateField("businessCenters", validBusinessCenters);
		}
	};

	const handleCategoriesChangeExtended = (value) => {
		handleCategoriesChange(value);
		const validBusinessCenters = filteredBusinessCenters.filter((bc) => bc.category_id && value.includes(bc.category_id)).map((bc) => bc.value);
		if (validBusinessCenters.length !== formData.businessCenters?.length) {
			onUpdateField("businessCenters", validBusinessCenters);
		}
	};

	return (
		<div className={styles.section}>
			<div className={styles.fieldsGrid}>
				<CustomInputSelect
					label="Campos"
					name="estatesParents"
					options={lookupOptions.estatesParents}
					value={formData.estatesParents || []}
					onChange={handleEstatesParentsChange}
					isMulti
					col={6}
				/>

				<CustomInputSelect
					label="Bodegas"
					name="estates"
					options={filteredEstates}
					value={formData.estates || []}
					onChange={handleEstatesChange}
					isMulti
					col={6}
				/>

				{isApplication && (
					<>
						<CustomInputSelect
							label="Categorías"
							name="categories"
							options={filteredCategories}
							value={formData.categories || []}
							onChange={handleCategoriesChangeExtended}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Especies"
							name="species"
							options={filteredSpecies}
							value={formData.species || []}
							onChange={handleSpeciesChange}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Variedades"
							name="varieties"
							options={filteredVarieties}
							value={formData.varieties || []}
							onChange={handleVarietiesChange}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Cuarteles"
							name="quarters"
							options={filteredQuarters}
							value={formData.quarters || []}
							onChange={(value) => onUpdateField("quarters", value)}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Centros de Costo"
							name="business_centers"
							options={filteredBusinessCenters}
							value={formData.businessCenters || []}
							onChange={(value) => onUpdateField("businessCenters", value)}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Operadores"
							name="operators"
							options={lookupOptions.operators}
							value={formData.operators || []}
							onChange={(value) => onUpdateField("operators", value)}
							isMulti
							col={6}
						/>
					</>
				)}

				{isTransfer && (
					<CustomInputSelect
						label="Transportistas"
						name="hauliers"
						options={lookupOptions.hauliers}
						value={formData.hauliers || []}
						onChange={(value) => onUpdateField("hauliers", value)}
						isMulti
						col={6}
					/>
				)}
			</div>
		</div>
	);
};

export default IORelations;
